import axios from "axios";
import { AxiosHeaders } from "axios";
import { ensureFreshToken } from "./keycloak";
import type {
  ComplianceCaseCreate,
  ComplianceCaseDetail,
  ComplianceCaseSummary,
  ComplianceCaseUpdate,
  ComplianceDashboardStats,
  ComplianceDocument,
  CollaboratorInviteCreate,
  CollaboratorInvitePublic,
  PassportSharePublic,
  ShareRequest,
  SupplierInviteCreate,
  SupplierInvitePublic,
  ValidationResult,
} from "./compliance/types";

export const api = axios.create({
  baseURL: "/api/backend",
  timeout: 60000,
});

api.interceptors.request.use(async (config) => {
  const url = config.url || "";
  if (
    url.startsWith("/supplier-invites/") ||
    url.startsWith("/passport-shares/") ||
    (url.startsWith("/collaborator-invites/") && !url.endsWith("/accept"))
  ) {
    return config;
  }
  const token = await ensureFreshToken();
  if (token) {
    const headers = AxiosHeaders.from(config.headers || {});
    headers.set("Authorization", `Bearer ${token}`);
    config.headers = headers;
  }
  return config;
});

export const supplierApi = axios.create({
  baseURL: "/api/backend",
  timeout: 60000,
});

export const publicApi = axios.create({
  baseURL: "/api/backend",
  timeout: 60000,
});

export async function listComplianceCases(clientOrg?: string): Promise<ComplianceCaseSummary[]> {
  const { data } = await api.get<ComplianceCaseSummary[]>("/compliance-cases", {
    params: clientOrg?.trim() ? { client_org: clientOrg.trim() } : undefined,
  });
  return data;
}

export async function getComplianceDashboardStats(): Promise<ComplianceDashboardStats> {
  const { data } = await api.get<ComplianceDashboardStats>("/compliance-cases/stats");
  return data;
}

export async function createComplianceCase(
  body: ComplianceCaseCreate
): Promise<ComplianceCaseDetail> {
  const { data } = await api.post<ComplianceCaseDetail>("/compliance-cases", body);
  return data;
}

export async function getComplianceCase(id: string): Promise<ComplianceCaseDetail> {
  const { data } = await api.get<ComplianceCaseDetail>(`/compliance-cases/${id}`);
  return data;
}

export async function updateComplianceCase(
  id: string,
  body: ComplianceCaseUpdate
): Promise<ComplianceCaseDetail> {
  const { data } = await api.patch<ComplianceCaseDetail>(`/compliance-cases/${id}`, body);
  return data;
}

export async function uploadComplianceDocument(
  caseId: string,
  category: string,
  file: File
): Promise<ComplianceDocument> {
  const form = new FormData();
  form.append("category", category);
  form.append("file", file);
  const { data } = await api.post<ComplianceDocument>(
    `/compliance-cases/${caseId}/documents`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

export async function attachSamplePack(caseId: string): Promise<ComplianceCaseDetail> {
  const { data } = await api.post<ComplianceCaseDetail>(
    `/compliance-cases/${caseId}/sample-pack`
  );
  return data;
}

export async function deleteComplianceDocument(caseId: string, docId: string): Promise<void> {
  await api.delete(`/compliance-cases/${caseId}/documents/${docId}`);
}

export function complianceDocumentDownloadPath(caseId: string, docId: string): string {
  return `/compliance-cases/${caseId}/documents/${docId}/download`;
}

export async function fetchComplianceDocumentBlob(
  caseId: string,
  docId: string
): Promise<{ blob: Blob; contentType: string }> {
  const { data, headers } = await api.get<Blob>(complianceDocumentDownloadPath(caseId, docId), {
    responseType: "blob",
  });
  const contentType =
    (typeof headers["content-type"] === "string" ? headers["content-type"] : undefined) ??
    "application/octet-stream";
  return { blob: data, contentType };
}

export async function validateComplianceCase(caseId: string): Promise<ValidationResult> {
  const { data } = await api.post<ValidationResult>(`/compliance-cases/${caseId}/validate`);
  return data;
}

export async function generatePassport(caseId: string): Promise<ComplianceCaseDetail> {
  const { data } = await api.post<ComplianceCaseDetail>(`/compliance-cases/${caseId}/passport`, {
    responsibility_acknowledged: true,
  });
  return data;
}

export async function downloadCompliancePassportVersion(
  caseId: string,
  version: number
): Promise<void> {
  const { data, headers } = await api.get<Blob>(
    `/compliance-cases/${caseId}/passports/${version}/download`,
    { responseType: "blob" }
  );
  const contentType =
    (typeof headers["content-type"] === "string" ? headers["content-type"] : undefined) ??
    "application/zip";
  const filename =
    parseContentDispositionFilename(
      typeof headers["content-disposition"] === "string" ? headers["content-disposition"] : undefined
    ) ?? `dds-passport-${caseId}-v${version}.zip`;
  const blob = new Blob([data], { type: contentType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function passportDownloadUrl(caseId: string): string {
  return `/api/backend/compliance-cases/${caseId}/passport/download`;
}

/** Public ZIP download URL path (no Keycloak). Prefer share_deliveries[].download_url from API. */
export function passportShareDownloadUrl(token: string): string {
  return `/api/backend/passport-shares/${token}/download`;
}

/** US-403: SPA read-only dossier page (absolute for copy/share). */
export function passportShareDossierUrl(token: string): string {
  return `${window.location.origin}/share/${token}`;
}

export async function getPassportSharePublic(token: string): Promise<PassportSharePublic> {
  const { data } = await publicApi.get<PassportSharePublic>(`/passport-shares/${token}`);
  return data;
}

function parseContentDispositionFilename(header: string | undefined): string | undefined {
  if (!header) return undefined;
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(header);
  return match?.[1];
}

export async function downloadCompliancePassport(caseId: string): Promise<void> {
  const { data, headers } = await api.get<Blob>(
    `/compliance-cases/${caseId}/passport/download`,
    { responseType: "blob" }
  );
  const contentType =
    (typeof headers["content-type"] === "string" ? headers["content-type"] : undefined) ??
    "application/zip";
  const filename =
    parseContentDispositionFilename(
      typeof headers["content-disposition"] === "string" ? headers["content-disposition"] : undefined
    ) ?? `dds-passport-${caseId}.zip`;
  const blob = new Blob([data], { type: contentType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function sharePassport(caseId: string, body: ShareRequest): Promise<ComplianceCaseDetail> {
  const { data } = await api.post<ComplianceCaseDetail>(
    `/compliance-cases/${caseId}/share`,
    body
  );
  return data;
}

export async function inviteSupplier(
  caseId: string,
  body: SupplierInviteCreate
): Promise<ComplianceCaseDetail> {
  const { data } = await api.post<ComplianceCaseDetail>(
    `/compliance-cases/${caseId}/supplier-invites`,
    body
  );
  return data;
}

export async function inviteCollaborator(
  caseId: string,
  body: CollaboratorInviteCreate
): Promise<ComplianceCaseDetail> {
  const { data } = await api.post<ComplianceCaseDetail>(
    `/compliance-cases/${caseId}/collaborator-invites`,
    body
  );
  return data;
}

export async function getCollaboratorInvite(token: string): Promise<CollaboratorInvitePublic> {
  const { data } = await publicApi.get<CollaboratorInvitePublic>(`/collaborator-invites/${token}`);
  return data;
}

export async function acceptCollaboratorInvite(token: string): Promise<CollaboratorInvitePublic> {
  const { data } = await api.post<CollaboratorInvitePublic>(`/collaborator-invites/${token}/accept`);
  return data;
}

export async function getSupplierInvite(token: string): Promise<SupplierInvitePublic> {
  const { data } = await supplierApi.get<SupplierInvitePublic>(`/supplier-invites/${token}`);
  return data;
}

export async function supplierUploadDocument(
  token: string,
  category: string,
  file: File
): Promise<ComplianceDocument> {
  const form = new FormData();
  form.append("category", category);
  form.append("file", file);
  const { data } = await supplierApi.post<ComplianceDocument>(
    `/supplier-invites/${token}/documents`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

/** US-505: download portfolio report as CSV or HTML. */
export async function downloadPortfolioReport(
  format: "csv" | "html",
  clientOrg?: string,
  locale?: string
): Promise<void> {
  const { data, headers } = await api.get<Blob>("/compliance-cases/reports/portfolio", {
    params: {
      format,
      client_org: clientOrg?.trim() || undefined,
      locale: locale || undefined,
    },
    responseType: "blob",
    headers: locale ? { "X-UI-Locale": locale } : undefined,
  });
  const contentType =
    (typeof headers["content-type"] === "string" ? headers["content-type"] : undefined) ??
    (format === "html" ? "text/html" : "text/csv");
  const filename =
    parseContentDispositionFilename(
      typeof headers["content-disposition"] === "string" ? headers["content-disposition"] : undefined
    ) ?? `dds-portfolio.${format}`;
  const blob = new Blob([data], { type: contentType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** US-505: queue portfolio digest email via Redpanda/SMTP path. */
export async function sendPortfolioDigest(clientOrg?: string, locale?: string): Promise<void> {
  await api.post("/compliance-cases/reports/portfolio/digest", {
    client_org: clientOrg?.trim() || undefined,
    locale: locale || undefined,
  });
}

/** US-504: preview templated email subject/body. */
export async function previewEmailTemplate(
  template: string,
  locale: string
): Promise<{ template: string; locale: string; subject: string; body: string }> {
  const { data } = await api.get<{
    template: string;
    locale: string;
    subject: string;
    body: string;
  }>("/compliance/email-templates/preview", { params: { template, locale } });
  return data;
}

export type ServerNotificationRow = {
  id: string;
  event_id: string;
  event_type: string;
  title: string;
  body: string | null;
  payload: string | null;
  is_read?: boolean;
  created_at: string;
};

function notificationsClient() {
  return axios.create({
    baseURL: "/api/notifications",
    timeout: 15000,
  });
}

/** US-507 / US-1006: poll notification-center for server-backed events. */
export async function listServerNotifications(): Promise<ServerNotificationRow[]> {
  const token = await ensureFreshToken();
  const { data } = await notificationsClient().get<ServerNotificationRow[]>("/notifications", {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return Array.isArray(data) ? data : [];
}

/** US-1006: mark all server bell rows read for the current user. */
export async function markAllServerNotificationsRead(): Promise<void> {
  const token = await ensureFreshToken();
  await notificationsClient().post(
    "/notifications/mark-all-read",
    {},
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
  );
}

/** US-1000 / US-1001: authenticated user notification profile. */
export type UserProfile = {
  user_id: string;
  locale: string;
  email: string | null;
  telegram_chat_id: string | null;
  email_enabled: boolean;
  telegram_enabled: boolean;
  bell_enabled: boolean;
  first_login_at: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type UserProfileUpdate = {
  locale?: string;
  email?: string | null;
  telegram_chat_id?: string | null;
  email_enabled?: boolean;
  telegram_enabled?: boolean;
  bell_enabled?: boolean;
};

export async function getUserProfile(): Promise<UserProfile> {
  const { data } = await api.get<UserProfile>("/users/me/profile");
  return data;
}

export async function patchUserProfile(body: UserProfileUpdate): Promise<UserProfile> {
  const { data } = await api.patch<UserProfile>("/users/me/profile", body);
  return data;
}
